/**
 * SSO Service – Keycloak OIDC Integration (Variante B)
 *
 * Handles the Authorization Code + PKCE flow with Keycloak.
 * After receiving the authorization code, it is sent to our own backend
 * (`/portal/sso/callback`) which exchanges the code for a Keycloak token,
 * extracts user data from the ID token claims, and returns our standard Portal JWT.
 *
 * User management happens exclusively in Keycloak — no local user database
 * or JIT-provisioning is involved. The backend simply translates Keycloak
 * identity claims into the internal JWT format used by our APIs.
 *
 * This keeps the token format unified — the rest of the app doesn't need
 * to know whether login happened via email/password or SSO.
 *
 * Keycloak Realm: https://auth.meesenburg-digital.de/realms/digital
 * Client ID: snaphub-meesenburg-digital-de (public client, PKCE)
 */

import { storageAdapter } from '../../infrastructure/storage-adapter';
import { eventBus } from '../../infrastructure/event-bus';
import { decodeTokenPayload, resolveRoleFromPayload } from './auth.service';
import type { LoginResponse } from '../../shared/models/login-response.model';
import type { LoginUser } from '../../shared/models/login-user.model';

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

function getSsoAuthority(): string {
  try {
    return (import.meta.env?.VITE_SSO_AUTHORITY as string | undefined) ?? 'https://auth.meesenburg-digital.de/realms/digital';
  } catch {
    return 'https://auth.meesenburg-digital.de/realms/digital';
  }
}

function getSsoClientId(): string {
  try {
    return (import.meta.env?.VITE_SSO_CLIENT_ID as string | undefined) ?? 'snaphub-meesenburg-digital-de';
  } catch {
    return 'snaphub-meesenburg-digital-de';
  }
}

function getSsoProvider(): string {
  try {
    return (import.meta.env?.VITE_SSO_PROVIDER as string | undefined) ?? 'snaphub-meesenburg-digital-de';
  } catch {
    return 'snaphub-meesenburg-digital-de';
  }
}

function getApiBaseUrl(): string {
  try {
    return (import.meta.env?.VITE_AUTH_API_BASE_URL as string | undefined) ?? '';
  } catch {
    return '';
  }
}

const SSO_STORAGE_KEYS = {
  codeVerifier: 'snaphub_sso_code_verifier',
  state: 'snaphub_sso_state',
  nonce: 'snaphub_sso_nonce',
} as const;

// ---------------------------------------------------------------------------
// PKCE Helpers
// ---------------------------------------------------------------------------

/**
 * Generate a cryptographically secure random string for PKCE code_verifier.
 * RFC 7636 requires 43–128 characters from [A-Z, a-z, 0-9, -, ., _, ~].
 */
function generateRandomString(length: number = 64): string {
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Create the PKCE code_challenge from a code_verifier using SHA-256.
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
}

/**
 * Base64url-encode a byte array (no padding, URL-safe alphabet).
 */
function base64UrlEncode(buffer: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < buffer.byteLength; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

// ---------------------------------------------------------------------------
// SSO Service
// ---------------------------------------------------------------------------

export class SsoService {
  private authority: string;
  private clientId: string;
  private apiBaseUrl: string;

  constructor() {
    this.authority = getSsoAuthority();
    this.clientId = getSsoClientId();
    this.apiBaseUrl = getApiBaseUrl();
  }

  // -- Public API -----------------------------------------------------------

  /**
   * Get the redirect URI for SSO callback.
   * Uses a real path (not hash-based) because Keycloak does not allow
   * URL fragments in redirect URIs. The SPA fallback (Caddy/Vite) ensures
   * this path loads index.html, and we detect it on app startup.
   */
  getRedirectUri(): string {
    return `${window.location.origin}/sso/callback`;
  }

  /**
   * Initiate the Keycloak SSO login flow.
   * Generates PKCE parameters, stores them in sessionStorage,
   * and redirects the browser to the Keycloak authorization endpoint.
   */
  async initiateLogin(): Promise<void> {
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateRandomString(32);
    const nonce = generateRandomString(32);

    // Persist PKCE state for the callback
    sessionStorage.setItem(SSO_STORAGE_KEYS.codeVerifier, codeVerifier);
    sessionStorage.setItem(SSO_STORAGE_KEYS.state, state);
    sessionStorage.setItem(SSO_STORAGE_KEYS.nonce, nonce);

    const authorizationEndpoint = `${this.authority}/protocol/openid-connect/auth`;

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.getRedirectUri(),
      response_type: 'code',
      scope: 'openid profile email',
      state,
      nonce,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `${authorizationEndpoint}?${params.toString()}`;
  }

  /**
   * Handle the callback from Keycloak after user authentication.
   *
   * Validates the state parameter, then sends the authorization code
   * to our backend `/portal/sso/callback` which:
   * 1. Exchanges the code at Keycloak's token endpoint
   * 2. Extracts user identity from the Keycloak ID token claims
   *    (users are managed exclusively in Keycloak, no local DB)
   * 3. Returns our standard Portal JWT (same format as /portal/login)
   *
   * @returns `null` on success, or an error message string on failure.
   */
  async handleCallback(code: string, state: string): Promise<{ error: string | null; user: LoginUser | null }> {
    const savedState = sessionStorage.getItem(SSO_STORAGE_KEYS.state);
    const codeVerifier = sessionStorage.getItem(SSO_STORAGE_KEYS.codeVerifier);

    // Always clean up SSO session data
    this.clearSsoSessionData();

    // Validate state to prevent CSRF
    if (!savedState || state !== savedState) {
      return { error: 'Anmeldefehler: Ungültiger State-Parameter. Bitte versuchen Sie es erneut.', user: null };
    }

    if (!codeVerifier) {
      return { error: 'Anmeldefehler: PKCE-Daten nicht gefunden. Bitte versuchen Sie es erneut.', user: null };
    }

    try {
      const response = await fetch(`${this.apiBaseUrl}/portal/sso/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: getSsoProvider(),
          code,
          code_verifier: codeVerifier,
          redirect_uri: this.getRedirectUri(),
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          return { error: 'Anmeldung fehlgeschlagen: Benutzer nicht berechtigt.', user: null };
        }
        if (response.status === 403) {
          return { error: 'Zugriff verweigert. Ihr Konto ist möglicherweise nicht freigeschaltet.', user: null };
        }
        return { error: `Anmeldung fehlgeschlagen (Fehler ${response.status}).`, user: null };
      }

      // Response matches the standard /portal/login format
      const data: LoginResponse = await response.json();

      // Support both field names: 'token' (Portal format) and 'access_token' (OAuth2/Keycloak format)
      const accessToken = data.token || data.access_token || '';

      if (accessToken) {
        storageAdapter.setAccessToken(accessToken);
      }
      if (data.refresh_token) {
        storageAdapter.setRefreshToken(data.refresh_token);
      }

      // Mark this session as SSO-authenticated (used to hide Portal-specific features)
      sessionStorage.setItem('snaphub_login_method', 'sso');

      // Build user object: merge response user data with token payload claims.
      // The backend may not include all fields in the user object (e.g. customerId),
      // so we always enrich from the JWT token payload as source of truth.
      let user: LoginUser | null = data.user ?? null;
      if (accessToken) {
        const payload = decodeTokenPayload(accessToken);
        if (payload) {
          user = {
            id: data.user?.id || payload.sub || '',
            email: data.user?.email || payload.email || payload.preferred_username || payload.name || '',
            role: data.user?.role || resolveRoleFromPayload(payload) || '',
            customerId: data.user?.customerId || payload.customerId || payload.customer_id || '',
            firstName: data.user?.firstName || payload.given_name || '',
            lastName: data.user?.lastName || payload.family_name || '',
            company: data.user?.company || '',
          };
        }
      }

      eventBus.emit('auth-state-changed', { authenticated: true });

      // Track SSO login for statistics (fire-and-forget)
      if (user || accessToken) {
        import('../admin/statistics-tracking.service').then(({ statisticsTrackingService }) => {
          statisticsTrackingService.trackLogin({
            userId: user?.id || '',
            userEmail: user?.email || '',
            role: user?.role || '',
            customerId: user?.customerId || '',
          });
        }).catch(() => { /* ignore */ });
      }

      return { error: null, user };
    } catch {
      return { error: 'Netzwerkfehler bei der Anmeldung. Bitte prüfen Sie Ihre Internetverbindung.', user: null };
    }
  }

  /**
   * Check if there is an active SSO flow in progress (PKCE data in session).
   */
  hasActiveFlow(): boolean {
    return sessionStorage.getItem(SSO_STORAGE_KEYS.state) !== null;
  }

  /**
   * Cancel an active SSO flow by clearing stored PKCE data.
   */
  cancelFlow(): void {
    this.clearSsoSessionData();
  }

  /**
   * Get the Keycloak end_session_endpoint for SSO logout.
   * Can be used to also terminate the Keycloak session on logout.
   */
  getLogoutUrl(idTokenHint?: string): string {
    const params = new URLSearchParams({
      client_id: this.clientId,
      post_logout_redirect_uri: window.location.origin,
    });

    if (idTokenHint) {
      params.set('id_token_hint', idTokenHint);
    }

    return `${this.authority}/protocol/openid-connect/logout?${params.toString()}`;
  }

  /**
   * Initiate the Keycloak password change flow via kc_action=UPDATE_PASSWORD.
   * Generates PKCE parameters, stores them in sessionStorage,
   * and redirects the browser to the Keycloak authorization endpoint
   * with kc_action=UPDATE_PASSWORD so the user is taken directly
   * to the "Passwort aktualisieren" page.
   */
  async initiatePasswordChange(): Promise<void> {
    const codeVerifier = generateRandomString(64);
    const codeChallenge = await generateCodeChallenge(codeVerifier);
    const state = generateRandomString(32);
    const nonce = generateRandomString(32);

    // Persist PKCE state for the callback
    sessionStorage.setItem(SSO_STORAGE_KEYS.codeVerifier, codeVerifier);
    sessionStorage.setItem(SSO_STORAGE_KEYS.state, state);
    sessionStorage.setItem(SSO_STORAGE_KEYS.nonce, nonce);

    const authorizationEndpoint = `${this.authority}/protocol/openid-connect/auth`;

    const params = new URLSearchParams({
      client_id: this.clientId,
      redirect_uri: this.getRedirectUri(),
      response_type: 'code',
      scope: 'openid profile email',
      state,
      nonce,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      kc_action: 'UPDATE_PASSWORD',
    });

    window.location.href = `${authorizationEndpoint}?${params.toString()}`;
  }

  // -- Private Helpers ------------------------------------------------------

  private clearSsoSessionData(): void {
    sessionStorage.removeItem(SSO_STORAGE_KEYS.codeVerifier);
    sessionStorage.removeItem(SSO_STORAGE_KEYS.state);
    sessionStorage.removeItem(SSO_STORAGE_KEYS.nonce);
  }
}

// ---------------------------------------------------------------------------
// Singleton instance
// ---------------------------------------------------------------------------

export const ssoService = new SsoService();
